import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { isApiError } from "@/data/api/client";
import { useAuth } from "@/features/auth/AuthContext";
import { partnerPushRepo, type StoredPushSubscription } from "@/data/repos/partnerPushRepo";
import { dropSubscription, findSubscription } from "@/data/push/browserPushSubscription";
import { observability } from "@/lib/observability";

/** `blocked` is the browser refusing, which no button here can undo — the
 *  partner has to lift it in the browser, so the switch says so instead of
 *  offering a click that cannot work. */
export type PushNotificationState =
  | "loading"
  | "unsupported"
  | "needs-install"
  | "blocked"
  | "on"
  | "off";

/** Consent, which the browser does not keep — and which belongs to the partner,
 *  not to the machine. A subscription is a fact about the browser profile: two
 *  partners sharing a computer would otherwise have the first one's endpoint
 *  read as the second one's answer, and the second one's reservations would
 *  reach the first one's browser. So the wish is stored per account, and it,
 *  never the presence of a subscription, is what the switch reflects. */
function wishKey(userId: string): string {
  return `lastgo.partner.push:${userId}`;
}

function wantsPush(userId: string): boolean {
  try {
    return window.localStorage.getItem(wishKey(userId)) === "on";
  } catch {
    return false;
  }
}

function rememberWish(userId: string, wanted: boolean): void {
  try {
    window.localStorage.setItem(wishKey(userId), wanted ? "on" : "off");
  } catch {
    // A browser that refuses storage still subscribes; it only forgets between
    // visits, which costs the self-heal and nothing else.
  }
}

export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Web Push on iOS exists only in a web app added to the Home Screen (16.4+), so
 *  the same missing `PushManager` that means "this browser cannot" elsewhere
 *  means "install the portal first" in an ordinary iPhone tab. iPadOS 13+ reports
 *  itself as a Mac, hence the touch-points test. */
function isIosOutsideHomeScreen(): boolean {
  const isIos =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isHomeScreenApp =
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia?.("(display-mode: standalone)").matches === true;
  return isIos && !isHomeScreenApp;
}

/** The VAPID key travels as base64url and `pushManager.subscribe` takes bytes. */
function applicationServerKey(publicKey: string): Uint8Array<ArrayBuffer> {
  const padded = publicKey.replace(/-/g, "+").replace(/_/g, "/");
  const binary = window.atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, "="));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function readSubscription(subscription: PushSubscription): StoredPushSubscription {
  const { endpoint, keys } = subscription.toJSON();
  if (!endpoint || !keys?.p256dh || !keys.auth) {
    throw new Error("the browser returned a subscription without its keys");
  }
  return { endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } };
}

async function subscribeBrowser(): Promise<PushSubscription> {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerKey(await partnerPushRepo.publicKey()),
  });
}

/** A 503 is the environment having no VAPID keys, which no second click fixes. */
function whyEnablingFailed(error: unknown): string {
  if (isApiError(error, "PUSH_NOT_CONFIGURED")) {
    return "Upozornění v prohlížeči zatím nemáme nastavená na serveru, takže je odsud zapnout nejde. Napište nám prosím na podpora@lastgo.eu.";
  }
  return "Upozornění se nepodařilo zapnout. Zkuste to prosím znovu.";
}

type HealedPushState = Extract<PushNotificationState, "blocked" | "on" | "off">;

/** Turns the partner's stored wish back into a subscription the API knows. It
 *  runs on every signed-in load, not only in Settings: signing out and back in
 *  drops the browser's subscription, and the browser renews its own with an
 *  endpoint the API has never seen. It never prompts and never speaks — a
 *  failure is logged and the page carries on. */
export function usePushSelfHeal(onSettled?: (state: HealedPushState) => void) {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId || !isPushSupported()) return;

    void (async () => {
      try {
        const existing = await findSubscription();
        const wanted = wantsPush(userId);
        // A subscription nobody signed in here asked for: the partner who used
        // this browser before, or an unsubscribe that failed after the switch
        // was turned off. Either way it must not survive as an answer.
        if (!wanted && existing) await dropSubscription(existing);
        if (Notification.permission === "denied") {
          onSettled?.("blocked");
          return;
        }
        if (!wanted || Notification.permission !== "granted") {
          onSettled?.("off");
          return;
        }
        // Re-sent rather than trusted: the endpoint may have been renewed since
        // the API last saw it, and the upsert is what heals that. Renewing a
        // subscription the browser dropped needs no prompt while permission
        // stands, so it happens silently.
        const subscription = existing ?? (await subscribeBrowser());
        await partnerPushRepo.subscribe(readSubscription(subscription));
        onSettled?.("on");
      } catch (failure) {
        observability.captureError(failure, { during: "push self-heal" });
        onSettled?.("off");
      }
    })();
  }, [userId, onSettled]);
}

export function usePushNotifications() {
  const [state, setState] = useState<PushNotificationState>("loading");
  const [isSwitching, setIsSwitching] = useState(false);
  const isMounted = useRef(true);
  const { user } = useAuth();
  const userId = user?.id ?? null;

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const show = useCallback((next: PushNotificationState) => {
    if (isMounted.current) setState(next);
  }, []);

  useEffect(() => {
    if (!isPushSupported()) setState(isIosOutsideHomeScreen() ? "needs-install" : "unsupported");
  }, []);

  usePushSelfHeal(show);

  const enable = useCallback(async () => {
    if (!userId) return;
    setIsSwitching(true);
    let subscription: PushSubscription | null = null;
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        rememberWish(userId, false);
        show(permission === "denied" ? "blocked" : "off");
        return;
      }
      subscription = (await findSubscription()) ?? (await subscribeBrowser());
      await partnerPushRepo.subscribe(readSubscription(subscription));
      rememberWish(userId, true);
      show("on");
    } catch (error) {
      // The browser subscription outlives a registration that failed, and the
      // partner was just told push is off — so it goes with the attempt rather
      // than sitting there receiving what nobody asked for.
      if (subscription) await subscription.unsubscribe().catch(() => false);
      rememberWish(userId, false);
      show("off");
      toast.error(whyEnablingFailed(error));
    } finally {
      setIsSwitching(false);
    }
  }, [userId, show]);

  const disable = useCallback(async () => {
    if (!userId) return;
    setIsSwitching(true);
    // Written before anything is undone: if the browser then refuses to
    // unsubscribe, the wish is already off, so the next load reads what is left
    // as an orphan and clears it instead of switching push back on.
    rememberWish(userId, false);
    try {
      const existing = await findSubscription();
      if (existing) await dropSubscription(existing);
      show("off");
    } catch {
      show("off");
      toast.error("Upozornění se nepodařilo vypnout. Zkuste to prosím znovu.");
    } finally {
      setIsSwitching(false);
    }
  }, [userId, show]);

  return { state, isSwitching, enable, disable };
}
